import React, { useEffect, useState, createContext, useContext } from 'react';
import { translations, Language } from './translations';
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>('en');
  useEffect(() => {
    const path = window.location.pathname;
    const pathParts = path.split('/');
    const langPrefix = pathParts[1] as Language;
    if (['en', 'ua', 'ru', 'ka'].includes(langPrefix)) {
      setLanguageState(langPrefix);
    } else {
      // If no valid language prefix, redirect to /en/ + current path
      // Avoid infinite loop if root is just /
      if (path === '/' || path === '') {
        window.history.replaceState(null, '', '/en/');
        setLanguageState('en');
      } else {
        // If path doesn't start with language, prepend /en/
        window.history.replaceState(null, '', `/en${path}`);
        setLanguageState('en');
      }
    }
  }, []);
  const setLanguage = (lang: Language) => {
    const path = window.location.pathname;
    const pathParts = path.split('/');
    // pathParts[0] is empty string, pathParts[1] is current language
    pathParts[1] = lang;
    const newPath = pathParts.join('/');
    window.history.pushState(null, '', newPath);
    setLanguageState(lang);
    // Force re-render of App to update routes if needed, though state change should trigger it
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  const t = (key: string) => {
    const keys = key.split('.');
    let value: any = translations[language];
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to English if key missing
        let fallback: any = translations['en'];
        for (const fk of keys) {
          if (fallback && fallback[fk]) {
            fallback = fallback[fk];
          } else {
            return key; // Return key if not found even in English
          }
        }
        return fallback;
      }
    }
    return value;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-285">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}